/**
 * Single source of truth for plan pricing + features.
 * Imported by Signup (PlanSelector) and UpgradeModal so they can never drift.
 * Keep these in sync with the Stripe price IDs configured in the backend.
 */
// Raised 2026-10-04 from $14.99 / $149.99 when Autopilot auto-apply shipped. The yearly price
// moved with it on purpose: holding it at $149.99 would have widened the annual discount from
// 17% to 37% at the exact moment the monthly price went up, so the cheaper plan would have
// undercut the rise. Both derived values below recompute, so there is one pair of numbers to edit.
export const MONTHLY_PRICE = 19.99;
export const YEARLY_PRICE = 199.99;
export const YEARLY_AS_MONTHLY = (YEARLY_PRICE / 12).toFixed(2); // ~16.67
export const YEARLY_SAVING_PCT = Math.round((1 - YEARLY_PRICE / (MONTHLY_PRICE * 12)) * 100); // ~17

// Canonical feature comparison. `basic`/`premium`: true = included, false = not,
// string = a specific value to display.
// A row may instead give `basicText` / `premiumText`, a COMPLETE phrase replacing value+label.
// That exists for Autopilot and little else: the two plans measure it over different periods —
// 15 once, ever, versus 50 every month — and "{value} {label}" cannot express that, because the
// label is shared. Forcing it into the old shape would mean either dropping the period (so the
// free row reads as monthly beside a monthly one) or inventing a label true of neither plan.
export const PLAN_FEATURES = [
  // Autopilot leads. It was MISSING from this list entirely until 2026-10-25 — the feature the
  // 2026-10-04 price rise was justified by did not appear in the comparison that justified it.
  //
  // The numbers mirror AI_APPLY_LIMITS in backend/server.py (basic 15 "lifetime", premium 50
  // "month"). If those env-configurable limits change, these strings become a promise the product
  // will refuse — backend/tests/test_signup_plans.py pins them to the backend.
  {
    label: "Autopilot applications",
    // "(one-time)" is not decoration. The 15 never reload: the backend stores them as a LIFETIME
    // allowance with no reset date, and deliberately refuses to tell those users a quota "resets
    // on the 1st". Beside a row reading "every month", anything vaguer reads as monthly.
    basic: "15", basicText: "15 Autopilot applications (one-time)",
    premium: "50", premiumText: "50 Autopilot applications every month",
  },
  { label: "Mock interviews / month", basic: "2",   premium: "Unlimited" },
  { label: "Resume analyzer",         basic: true,  premium: true        },
  { label: "Question retakes",        basic: false, premium: true        },
  { label: "Premium roles (1,000+)",  basic: false, premium: true        },
];

// How a row reads for one plan. One formatter, four call sites (both signup cards, the upgrade
// modal, the subscription page) — they each rendered this inline and identically, which is how
// three of them quietly keep the old shape when the fourth learns a new one.
export function planFeatureText(feature, plan) {
  const explicit = plan === "premium" ? feature.premiumText : feature.basicText;
  if (explicit) return explicit;
  const value = plan === "premium" ? feature.premium : feature.basic;
  return typeof value === "string" ? `${value} ${feature.label}` : feature.label;
}

// Contextual headline shown at the top of the upgrade modal, keyed by the
// `reason` each trigger passes. Falls back to `default`.
export const UPGRADE_REASONS = {
  monthly_interviews: "You've used your 2 free interviews this month.",
  premium_role:       "This role is part of our Premium role bank.",
  ats_optimize:       "AI resume optimization is a Premium feature.",
  retake:             "Question retakes are a Premium feature.",
  resume_slot:        "The free plan includes 1 saved resume — upgrade for unlimited.",
  live_search:        "Search live for any job title — Premium looks beyond your daily matches.",
  autopilot_limit:    "You've used all 15 of your Autopilot applications. Premium gives you 50 every month.",
  default:            "Unlock everything with Premium.",
};

export const upgradeReasonText = (reason) => UPGRADE_REASONS[reason] || UPGRADE_REASONS.default;
